/* VOLAN enhanced product-media modal viewer.
   Built on the existing Dawn modal and Volan gallery controls.
   Image-only: 1x / 1.5x / 2x / 3x / 4x / 5x; pinch, drag, wheel, double-tap.
   Video, external video and 3D retain their existing playback behavior.
*/
(() => {
  'use strict';
  const LEVELS = [1, 1.5, 2, 3, 4, 5];
  const SWIPE_MIN = 48;
  const clamp = (value, low, high) => Math.min(high, Math.max(low, value));

  function slidesFor(modal) {
    const viewport = modal.querySelector('.product-media-modal__content');
    return viewport ? [...viewport.children].filter(el => el.hasAttribute('data-media-id')) : [];
  }
  function imageFor(slide) {
    if (!slide) return null;
    if (slide.matches('img[data-media-id]')) return slide;
    if (slide.matches('deferred-media, product-model, video, iframe')) return null;
    if (slide.querySelector('deferred-media, video, iframe, model-viewer')) return null;
    return slide.querySelector('img');
  }
  function pauseSlide(slide) {
    slide.querySelectorAll('video, audio').forEach(media => {
      try { media.pause(); } catch (_) { /* A player may have been unloaded. */ }
    });
    slide.querySelectorAll('product-model').forEach(model => {
      if (model.modelViewerUI) model.modelViewerUI.pause();
    });
  }
  function loadDeferredMedia(slide) {
    const media = slide.matches('deferred-media, product-model') ? slide : null;
    if (media && typeof media.loadContent === 'function' && !media.hasAttribute('loaded')) {
      media.loadContent(false);
    }
  }

  class VolanViewer {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('.product-media-modal__content');
      this.chrome = root.querySelector('[data-volan-viewer-controls]');
      this.previous = root.querySelector('[data-volan-viewer-prev]');
      this.next = root.querySelector('[data-volan-viewer-next]');
      this.dots = root.querySelector('[data-volan-viewer-dots]');
      this.zoomControls = root.querySelector('[data-volan-zoom-controls]');
      this.zoomIn = root.querySelector('[data-volan-zoom-in]');
      this.zoomOut = root.querySelector('[data-volan-zoom-out]');
      this.zoomReset = root.querySelector('[data-volan-zoom-reset]');
      this.zoomValue = root.querySelector('[data-volan-zoom-value]');
      this.index = 0;
      this.scale = 1;
      this.pan = { x: 0, y: 0 };
      this.pointers = new Map();
      this.dragStart = null;
      this.pinchStart = null;
      this.swipeStart = null;
      this.image = null;
      this.lastTap = 0;
    }

    init() {
      if (!this.viewport || !this.chrome || !this.zoomControls) return;
      this.root.dataset.volanViewerReady = 'true';
      // Dawn closes the modal on a bubbling pointerup. Stop it at the viewer
      // surfaces in BUBBLE phase, not on the modal in capture phase.
      // Capture-phase stopping swallowed pointerup before pointerEnd() could
      // clear dragStart, making the image continue panning during mouse hover.
      const keepViewerOpen = event => event.stopPropagation();
      this.viewport.addEventListener('pointerup', keepViewerOpen);
      this.chrome.addEventListener('pointerup', keepViewerOpen);
      this.zoomControls.addEventListener('pointerup', keepViewerOpen);

      this.previous?.addEventListener('click', () => this.move(-1));
      this.next?.addEventListener('click', () => this.move(1));
      this.zoomIn?.addEventListener('click', () => this.step(1));
      this.zoomOut?.addEventListener('click', () => this.step(-1));
      this.zoomReset?.addEventListener('click', () => this.setZoom(1));

      this.root.addEventListener('keydown', event => {
        if (!this.root.hasAttribute('open')) return;
        if (event.target.closest('input,textarea,select')) return;
        if (event.key === 'ArrowLeft' && this.scale === 1) { event.preventDefault(); this.move(-1); }
        if (event.key === 'ArrowRight' && this.scale === 1) { event.preventDefault(); this.move(1); }
        if (event.key === '+' || event.key === '=') { event.preventDefault(); this.step(1); }
        if (event.key === '-') { event.preventDefault(); this.step(-1); }
        if (event.key === '0') { event.preventDefault(); this.setZoom(1); }
      });

      this.viewport.addEventListener('wheel', event => {
        if (!this.root.hasAttribute('open') || !this.image || !this.image.contains(event.target) && event.target !== this.image) return;
        event.preventDefault();
        this.step(event.deltaY < 0 ? 1 : -1);
      }, { passive: false });

      this.viewport.addEventListener('dblclick', event => {
        if (!this.root.hasAttribute('open') || !this.image || event.target !== this.image) return;
        event.preventDefault();
        this.setZoom(this.scale >= 2 ? 1 : 2);
      });

      this.viewport.addEventListener('pointerdown', event => this.pointerDown(event));
      this.viewport.addEventListener('pointermove', event => this.pointerMove(event));
      this.viewport.addEventListener('pointerup', event => this.pointerEnd(event));
      this.viewport.addEventListener('pointercancel', event => this.pointerEnd(event));
      // For pointerup outside the image when browser pointer capture is lost.
      window.addEventListener('pointerup', event => {
        if (this.pointers.has(event.pointerId)) this.pointerEnd(event);
      });
      window.addEventListener('pointercancel', event => {
        if (this.pointers.has(event.pointerId)) this.pointerEnd(event);
      });
      window.addEventListener('blur', () => {
        this.pointers.clear();
        this.dragStart = null;
        this.pinchStart = null;
        this.image?.classList.remove('is-panning');
      });

      this.viewport.addEventListener('touchstart', event => {
        if (this.scale > 1 || event.touches.length !== 1) { this.swipeStart = null; return; }
        this.swipeStart = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }, { passive: true });
      this.viewport.addEventListener('touchend', event => {
        if (this.scale > 1 || !this.swipeStart || event.changedTouches.length !== 1) return;
        const touch = event.changedTouches[0];
        const dx = touch.clientX - this.swipeStart.x;
        const dy = touch.clientY - this.swipeStart.y;
        this.swipeStart = null;
        if (Math.abs(dx) >= SWIPE_MIN && Math.abs(dx) > Math.abs(dy) * 1.5) this.move(dx < 0 ? 1 : -1);
      }, { passive: true });

      this.viewport.addEventListener('click', event => {
        if (!this.image || event.target !== this.image || event.detail === 0) return;
        // Mobile double tap to zoom, without changing desktop single-click behavior.
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        const now = Date.now();
        if (now - this.lastTap < 300) { event.preventDefault(); this.setZoom(this.scale >= 2 ? 1 : 2); }
        this.lastTap = now;
      });

      const observer = new MutationObserver(mutations => {
        if (mutations.some(m => m.attributeName === 'open')) {
          if (this.root.hasAttribute('open')) this.sync(true);
          else this.resetZoom();
        }
      });
      observer.observe(this.root, { attributes: true, attributeFilter: ['open'] });
      if (this.root.hasAttribute('open')) this.sync(true);
      window.addEventListener('resize', () => { if (this.root.hasAttribute('open')) this.setZoom(this.scale); });
    }

    activeIndex(slides) {
      const active = slides.findIndex(slide => slide.classList.contains('active'));
      if (active >= 0) return active;
      const openerId = this.root.openedBy?.getAttribute?.('data-media-id');
      const selected = slides.findIndex(slide => slide.getAttribute('data-media-id') === openerId);
      return selected >= 0 ? selected : 0;
    }

    sync(isOpening) {
      const slides = slidesFor(this.root);
      if (!slides.length) return;
      this.index = this.activeIndex(slides);
      this.chrome.hidden = slides.length < 2;
      if (this.dots) {
        this.dots.replaceChildren();
        slides.forEach((_, position) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'product-media-modal__dot';
          dot.setAttribute('aria-label', `View product media ${position + 1} of ${slides.length}`);
          if (position === this.index) dot.setAttribute('aria-current', 'true');
          dot.addEventListener('click', () => this.go(position));
          this.dots.append(dot);
        });
      }
      // Dawn selects the initial slide when opening. Selecting it explicitly
      // here also handles openers without a matching active class.
      if (isOpening) this.activate(slides, this.index, true);
    }

    move(step) {
      const slides = slidesFor(this.root);
      if (slides.length < 2) return;
      this.go((this.index + step + slides.length) % slides.length);
    }
    go(position) {
      const slides = slidesFor(this.root);
      if (!slides.length) return;
      this.index = (position + slides.length) % slides.length;
      this.activate(slides, this.index, false);
    }
    activate(slides, index, isOpening) {
      this.resetZoom();
      slides.forEach((slide, position) => {
        if (position === index) {
          slide.classList.add('active');
          if (!isOpening) loadDeferredMedia(slide);
        } else {
          pauseSlide(slide);
          slide.classList.remove('active');
        }
      });
      [...(this.dots?.children || [])].forEach((dot, position) => {
        if (position === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      this.image = imageFor(slides[index]);
      this.zoomControls.hidden = !this.image;
      if (this.image) {
        this.image.classList.add('volan-zoomable-image');
        this.image.setAttribute('draggable', 'false');
      }
      this.updateZoomUI();
    }

    resetZoom() {
      if (this.image) {
        this.image.style.transform = '';
        this.image.classList.remove('is-panning', 'is-zoomed');
      }
      this.scale = 1;
      this.pan = { x: 0, y: 0 };
      this.pointers.clear();
      this.dragStart = null;
      this.pinchStart = null;
      this.swipeStart = null;
    }

    panLimits(scale) {
      if (!this.image || !this.viewport) return { x: 0, y: 0 };
      // offsetWidth/Height are untransformed, so clamp to the visible viewport.
      const width = this.image.offsetWidth;
      const height = this.image.offsetHeight;
      return {
        x: Math.max(0, (width * scale - this.viewport.clientWidth) / 2),
        y: Math.max(0, (height * scale - this.viewport.clientHeight) / 2)
      };
    }
    renderTransform() {
      if (!this.image) return;
      const limit = this.panLimits(this.scale);
      this.pan.x = clamp(this.pan.x, -limit.x, limit.x);
      this.pan.y = clamp(this.pan.y, -limit.y, limit.y);
      this.image.style.transform = `translate3d(${this.pan.x}px, ${this.pan.y}px, 0) scale(${this.scale})`;
      this.image.classList.toggle('is-zoomed', this.scale > 1);
      this.updateZoomUI();
    }
    setZoom(value) {
      if (!this.image) return;
      this.scale = clamp(value, 1, LEVELS[LEVELS.length - 1]);
      if (this.scale > 1 && this.image.dataset.zoomSrc && this.image.dataset.zoomFullLoaded !== '1') {
        this.image.srcset = '';
        this.image.src = this.image.dataset.zoomSrc;
        this.image.dataset.zoomFullLoaded = '1';
      }
      if (this.scale <= 1.001) { this.scale = 1; this.pan = { x: 0, y: 0 }; }
      this.renderTransform();
    }
    step(direction) {
      if (!this.image) return;
      let index;
      if (direction > 0) index = LEVELS.findIndex(level => level > this.scale + 0.01);
      else index = [...LEVELS].reverse().findIndex(level => level < this.scale - 0.01);
      const next = direction > 0 ? (index < 0 ? LEVELS.at(-1) : LEVELS[index])
        : (index < 0 ? 1 : [...LEVELS].reverse()[index]);
      this.setZoom(next);
    }
    updateZoomUI() {
      if (this.zoomValue) this.zoomValue.textContent = `${Math.round(this.scale * 100)}%`;
      if (this.zoomOut) this.zoomOut.disabled = !this.image || this.scale <= 1;
      if (this.zoomIn) this.zoomIn.disabled = !this.image || this.scale >= LEVELS.at(-1);
      if (this.zoomReset) this.zoomReset.disabled = !this.image || this.scale <= 1;
    }

    pointerDown(event) {
      if (!this.root.hasAttribute('open') || !this.image) return;
      if (event.target !== this.image) return;
      // A mouse must actively hold its primary button to pan. Touch pointers
      // are still tracked at 1x so two-finger pinch can start normally.
      if (event.pointerType === 'mouse' &&
          (event.button !== 0 || !(event.buttons & 1) || this.scale <= 1)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pointers.size === 1 && this.scale > 1) {
        this.dragStart = { x: event.clientX - this.pan.x, y: event.clientY - this.pan.y };
        this.image.classList.add('is-panning');
      }
      if (this.pointers.size === 2) {
        this.swipeStart = null;
        const [a, b] = [...this.pointers.values()];
        this.pinchStart = { distance: Math.hypot(a.x - b.x, a.y - b.y), scale: this.scale };
        this.dragStart = null;
      }
      if (this.scale > 1 || this.pointers.size >= 2) {
        try { this.image.setPointerCapture(event.pointerId); } catch (_) { /* optional */ }
      }
    }
    pointerMove(event) {
      if (!this.pointers.has(event.pointerId) || !this.image) return;
      // Safety net for a missed mouse pointerup (e.g. released outside window):
      // NEVER pan from hover-only mouse movement.
      if (event.pointerType === 'mouse' && !(event.buttons & 1)) {
        this.pointerEnd(event);
        return;
      }
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.pointers.size >= 2 && this.pinchStart) {
        const [a, b] = [...this.pointers.values()];
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        if (this.pinchStart.distance > 0) this.setZoom(this.pinchStart.scale * distance / this.pinchStart.distance);
      } else if (this.scale > 1 && this.dragStart) {
        this.pan.x = event.clientX - this.dragStart.x;
        this.pan.y = event.clientY - this.dragStart.y;
        this.renderTransform();
      }
    }
    pointerEnd(event) {
      this.pointers.delete(event.pointerId);
      if (this.pointers.size < 2) this.pinchStart = null;
      if (this.pointers.size === 0) {
        this.dragStart = null;
        this.image?.classList.remove('is-panning');
      } else if (this.pointers.size === 1) {
        const point = [...this.pointers.values()][0];
        this.dragStart = { x: point.x - this.pan.x, y: point.y - this.pan.y };
      }
    }
  }

  function init() {
    document.querySelectorAll('product-modal.product-media-modal').forEach(modal => {
      if (modal.dataset.volanViewerReady === 'true') return;
      new VolanViewer(modal).init();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
